"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Icons ---

/* straight arrow (matches Figma "left/right-arrow-alt"); currentColor = comp-text */
function ArrowIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      {dir === "left" ? (
        <path
          d="M20 12H4M10 6l-6 6 6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M4 12h16M14 6l6 6-6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  )
}

/* Figma play/pause: black 8×25 bars inside the 50px white circle */
function PauseIcon() {
  return (
    <svg width="20" height="25" viewBox="0 0 20 25" fill="#000" aria-hidden focusable="false">
      <rect x="1" y="0" width="8" height="25" rx="1.5" />
      <rect x="11" y="0" width="8" height="25" rx="1.5" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg width="20" height="25" viewBox="0 0 20 25" fill="#000" aria-hidden focusable="false">
      <path d="M2 1.5v22l18-11L2 1.5z" />
    </svg>
  )
}

// --- Viewport entrance (fires once per page load) ---

/* Gallery 5 had no entrance animation while its siblings all reveal on scroll
   (ASH-1896). The text fades and rises; the carousel slides in horizontally
   just behind it. */
function useInViewOnce() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 })
  const [reduceMotion, setReduceMotion] = useState(false)
  useEffect(() => {
    setReduceMotion(!!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
  }, [])
  /* reduced motion: render the end state immediately instead of animating in */
  return { ref, inView: inView || reduceMotion }
}

// --- Styled Components ---

const Section = styled.section`
  padding: 48px 20px 64px;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 120px;
  }
`

const Inner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    gap: 80px;
  }
`

/* first in JSX = right side in RTL (text on the right on desktop, top on mobile) */
const TextCol = styled.div<{ $in: boolean }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "16px")});
  transition: opacity 0.5s ease, transform 0.5s ease;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: start;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 580px;
    flex-shrink: 0;
  }
`

/* Rich-text title: a bold word renders underlined in the heading colour */
const Title = styled.h2`
  font-size: 40px;
  /* Figma 748:4363 / 748:4384 sets the title at 700, not 500 (ASH-1897). */
  font-weight: 700;
  line-height: 1.1;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  strong {
    font-weight: 700;
    text-decoration: none;
    display: inline-block;
    margin: 0;
    position: relative;
    &::after {
      content: "";
      position: absolute;
      bottom: -4px;
      inset-inline: 0;
      height: 6px;
      z-index: 1;
      background-color: currentColor;
      -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
      mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    }
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const Subtitle = styled.p`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-text);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

/* QA returned the fade-and-rise as "still stationary" — the slider itself has to
   travel horizontally. It starts half its own width off the reading start edge
   (the left in RTL, where the carousel bleeds off-screen) and slides back into
   place. transform is not mirrored by dir, so the sign is picked from $isRtl. */
const CarouselCol = styled.div<{ $in: boolean; $isRtl: boolean }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateX(${({ $in, $isRtl }) => ($in ? "0" : $isRtl ? "100%" : "-100%")});
  transition: opacity 0.6s ease 0.15s, transform 0.8s cubic-bezier(0.22, 0.61, 0.36, 1) 0.15s;
  display: flex;
  flex-direction: column;
  gap: 29px;
  min-width: 0;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 22px;
    flex: 1;
  }
`

/* Full-bleed to the screen's left edge, clipped on the right at the text boundary.
   The active slide is anchored to the reading start (right in RTL); earlier/later
   slides bleed off the left edge and never cross into the gap/text on the right.
   Negative inline-end margin cancels the Section's start padding so the bleed
   reaches the physical screen edge. */
const CarouselWrapper = styled.div`
  overflow: hidden;
  margin-inline-end: -20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-inline-end: -120px;
  }

  .swiper {
    overflow: visible;
    /* the global .swiper rule adds 50px top/bottom, which showed up as dead
       space between the image and the nav row (ASH-2185) */
    padding: 0;
  }

  /* fixed square slide per Figma: 280 mobile, 560 desktop (slidesPerView="auto") */
  .swiper-slide {
    width: 280px;

    ${(props) => props.theme.breakpoints.up("md")} {
      width: 560px;
    }
  }
`

const Slide = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;
`

const Media = styled(Image)`
  object-fit: cover;
`

const MediaVideo = styled.video`
  position: absolute;
  inset: 0;
  height: 100%;
  object-fit: cover;
`

const PlayPauseBtn = styled.button`
  position: absolute;
  bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  z-index: 2;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }
`

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Dot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 100px;
  padding: 0;
  cursor: pointer;
  border: ${({ active }) => (active ? "none" : "1px solid var(--comp-text)")};
  background-color: ${({ active }) => (active ? "var(--comp-text)" : "transparent")};

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 12px;
    height: 12px;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const Arrows = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ArrowBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid var(--comp-text);
  background: transparent;
  color: var(--comp-text);
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

// --- Types & parsing ---

type MediaItem = {
  url: string
  altText: string
}

function isVideoUrl(url: string): boolean {
  return url.endsWith(".mp4") || url.endsWith(".webm")
}

// --- Slide ---

function GallerySlide({ item, isRtl }: { item: MediaItem; isRtl: boolean }) {
  const analytics = useLandingAnalytics()
  const isVideo = isVideoUrl(item.url)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)

  const togglePlay = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const v = videoRef.current
    if (!v) return
    if (isPlaying) v.pause()
    else v.play()
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.gallery)
    setIsPlaying(!isPlaying)
  }

  return (
    <Slide>
      {isVideo ? (
        <MediaVideo
          ref={videoRef}
          src={getUmbracoMediaUrl(item.url)}
          autoPlay
          muted
          loop
          playsInline
        />
      ) : (
        <Media src={getUmbracoMediaUrl(item.url)} fill alt={item.altText || ""} sizes="560px" />
      )}
      {isVideo && (
        <PlayPauseBtn
          onClick={togglePlay}
          style={{ left: 16 }}
          aria-label={
            isPlaying
              ? isRtl
                ? "עצור תנועת הרכיב"
                : "Stop animation"
              : isRtl
              ? "הפעל תנועה רכיב"
              : "Play animation"
          }
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </PlayPauseBtn>
      )}
    </Slide>
  )
}

// --- Main Component ---

function Gallery5({ data }: Props) {
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const { ref: revealRef, inView } = useInViewOnce()
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const syncSwiperState = (swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex)
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }

  const topTitle = extractProperty(data, "topTitle") as string | ""
  const subtitle = extractProperty(data, "subtitle") as string | ""
  const mediaItems = extractProperty(data, "images", true) as any[] | undefined
  const items: MediaItem[] = (Array.isArray(mediaItems) ? mediaItems : [])
    .filter((m) => m?.url)
    .map((m) => ({ url: m.url as string, altText: (m.altText as string) || "" }))

  if (!topTitle && !subtitle && items.length === 0) return null

  const hasCarousel = items.length > 0
  const showNav = items.length > 1

  return (
    <Section ref={revealRef}>
      <Inner>
        <TextCol $in={inView}>
          {topTitle && <Title dangerouslySetInnerHTML={{ __html: topTitle }} />}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </TextCol>

        {hasCarousel && (
          <CarouselCol $in={inView} $isRtl={isRtl}>
            <CarouselWrapper>
              <Swiper
                onSwiper={(swiper) => {
                  setSwiperRef(swiper)
                  syncSwiperState(swiper)
                }}
                onSlideChange={syncSwiperState}
                onResize={syncSwiperState}
                dir={isRtl ? "rtl" : "ltr"}
                spaceBetween={24}
                slidesPerView="auto"
                /* CarouselWrapper cancels the Section gutter with a negative
                   inline-end margin so slides bleed off the screen edge while
                   scrolling. Nothing put that gutter back at the end of the
                   run, so the last slide came to rest flush against the edge
                   (ASH-2088). The offset restores it, matching the Section
                   padding at each breakpoint. */
                slidesOffsetAfter={20}
                breakpoints={{ 768: { spaceBetween: 32, slidesOffsetAfter: 120 } }}
              >
                {items.map((item, i) => (
                  <SwiperSlide key={i}>
                    <GallerySlide item={item} isRtl={isRtl} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </CarouselWrapper>

            {showNav && (
              <NavRow>
                {/* RTL: Arrows first in JSX → right side; Dots → left side (matches Figma) */}
                <Arrows>
                  <ArrowBtn
                    onClick={() => swiperRef?.slidePrev()}
                    disabled={isBeginning}
                    aria-label={isRtl ? "הקודם" : "Previous"}
                  >
                    <ArrowIcon dir={isRtl ? "right" : "left"} />
                  </ArrowBtn>
                  <ArrowBtn
                    onClick={() => swiperRef?.slideNext()}
                    disabled={isEnd}
                    aria-label={isRtl ? "הבא" : "Next"}
                  >
                    <ArrowIcon dir={isRtl ? "left" : "right"} />
                  </ArrowBtn>
                </Arrows>
                <Dots>
                  {items.map((_, i) => (
                    <Dot
                      key={i}
                      /* With slidesPerView="auto" the carousel stops scrolling
                         once the last slide is flush right, so activeIndex
                         never reaches the final index and the last dot stayed
                         inactive. Anchor to the end explicitly (ASH-1898). */
                      active={isEnd ? i === items.length - 1 : i === activeIndex}
                      onClick={() => swiperRef?.slideTo(i)}
                      aria-label={isRtl ? `תמונה ${i + 1}` : `Image ${i + 1}`}
                    />
                  ))}
                </Dots>
              </NavRow>
            )}
          </CarouselCol>
        )}
      </Inner>
    </Section>
  )
}

export default Gallery5
